import axios from 'axios';

/**
 * Single source of truth for "is this an *expected* client (4xx) error?"
 *
 * Sibling of `isTransientApiError` (`./transient.ts`). Where that helper answers
 * "is this a transient network/server hiccup?", this one answers "is this a 4xx
 * the app routinely provokes in normal use — an expired session, a validation
 * miss, a permission boundary — that does NOT indicate a code bug?".
 *
 * See docs/code/lib.md for the GlitchTip-flood motivation. A caller that knows
 * a given 4xx is a real bug (e.g. the #3738 FormData 422 contract bug) can
 * still force a report with `reportError(err, where, { expected: false })`.
 *
 * Only Axios errors with a response qualify. We gate on `axios.isAxiosError`
 * first, so a non-Axios value (a plain `Error` thrown by app code) returns
 * `false` — it is a real failure that must reach Sentry, not be silenced. This
 * mirrors `isTransientApiError` and project value 3 (fail visibly, never
 * silently): a blind `error as AxiosError` cast would itself be a fail-silent
 * pattern. Responseless Axios errors (timeouts, network failures) are NOT
 * expected-client — they belong to `isTransientApiError` — so they return
 * `false` here too.
 *
 * ## Policy — which 4xx are "expected"
 *
 * Grounded in how the codebase already treats each code (api/client.ts
 * interceptor, the per-endpoint suppressors, guest-portal handlers):
 *
 * | Status | Expected | Why |
 * |--------|----------|-----|
 * | 400 Bad Request        | yes | validation; caller surfaces field errors |
 * | 401 Unauthorized       | yes | expired session — the 26× flood; interceptor refreshes/redirects |
 * | 403 Forbidden          | yes | permission boundary — trailer-board layer, capability resync, guest portal |
 * | 404 Not Found          | yes | resource-not-found probes — payroll preview, guest check-in, sync |
 * | 409 Conflict           | yes | optimistic-concurrency race — the interceptor already toasts "modified by another user"; the user resolves it by refreshing, it is not a code defect |
 * | 413 Payload Too Large  | yes | upload validation (size) — user-correctable |
 * | 415 Unsupported Media  | yes | upload validation (type) — user-correctable |
 * | 422 Unprocessable      | yes | validation; real-contract 422s opt back in via { expected: false } |
 * | 429 Too Many Requests  | yes | rate-limit backpressure — expected, not a bug |
 *
 * Deliberately NOT expected (more often a real contract bug, so it keeps
 * reporting to Sentry by default):
 *
 * | 405 Method Not Allowed | a wrong HTTP verb is a client/contract bug, not normal traffic |
 *
 * Any 4xx not listed above (402, 405, 410, 418, 451, ...) is treated as NOT
 * expected and keeps reporting — we only silence codes with codebase evidence
 * that they are routine.
 */
const EXPECTED_CLIENT_STATUSES = new Set([400, 401, 403, 404, 409, 413, 415, 422, 429]);

export function isExpectedClientError(error: unknown): boolean {
  if (!axios.isAxiosError(error)) {
    return false;
  }

  const status = error.response?.status;
  if (status === undefined) {
    return false;
  }

  return EXPECTED_CLIENT_STATUSES.has(status);
}
